import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import GridActions from '@/components/Base/GridActions/actions'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { ValueCard } from '@/components/Base/ValueCard/ValueCard'
import { GFPADashboardNavigation } from '@/components/GFPA/GFPADashboardNavigation'
import { GFPASummaryChart } from '@/components/GFPA/SummaryReport/GFPASummaryChart'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IGfpaDurations,
  IGfpaSummaryDTO,
  IGfpaTotalReturn,
} from '@/interfaces/gfpaReport.interface'
import {
  clearGFPASummary,
  fetchGFPASummaryAsync,
} from '@/states/axiomReport.slice'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { formatNumber } from '@/utils/utils'
import { AgGridReact } from '@ag-grid-community/react'
import { MoreVert } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import {
  getColumnDefByReportType,
  getReportDuration,
  getReportTotalReturns,
} from '@services/axiomReport.service'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GFPAFactorContributionPage } from '../GFPAFactorContributionPage/GFPAFactorContributionPage'

export const GFPADashboardPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const gfpaSummary = useAppSelector((s) => s.axiomReport.gfpaReport.summary)
  const showLoader = useAppSelector((s) => s.axiomReport.showLoader)
  const [rowData, setRowData] = useState<IGfpaSummaryDTO[]>([])
  const [totalReturn, setTotalReturn] = useState<IGfpaTotalReturn>(
    {} as IGfpaTotalReturn,
  )
  const [loaderTR, setLoaderTR] = useState<boolean>(false)
  const [duration, setDuration] = useState<IGfpaDurations>({} as IGfpaDurations)
  const [loaderDuration, setLoaderDuration] = useState<boolean>(false)
  const [gridReference, setGridReference] = useState<AgGridReact>()

  const loader = () => {
    return (
      <Box
        display='grid'
        height='100%'
        alignItems='center'
        justifyContent='center'
        role='loading'
      >
        <CircularProgress color='primary' />
      </Box>
    )
  }

  useEffect(() => {
    if (
      Object.keys(selectedReport).length &&
      (selectedReport.reportType === 'GFPA' || selectedReport.reportType === 'GFPAH')
    ) {
      dispatch(fetchGFPASummaryAsync(selectedReport.reportId))
    } else {
      navigate('/')
    }
  }, [selectedReport])

  useEffect(() => {
    const fetchReportTotalReturn = async () => {
      try {
        setLoaderTR(true)
        const { reportType, reportId } = selectedReport
        const response = await getReportTotalReturns(reportType, reportId)
        setTotalReturn(response)
      } catch (err) {
        //setError(err.message || 'An error occurred while fetching the total return.');
      } finally {
        setLoaderTR(false)
      }
    }
    const fetchReportDuration = async () => {
      try {
        setLoaderDuration(true)
        const { reportType, reportId } = selectedReport
        const response = await getReportDuration(reportType, reportId)
        setDuration(response)
      } catch (err) {
        //setError(err.message || 'An error occurred while fetching the effective duration.');
      } finally {
        setLoaderDuration(false)
      }
    }
    if (Object.keys(selectedReport).length) {
      fetchReportTotalReturn()
      fetchReportDuration()
    }

    const isStoredInSession = sessionStorage.getItem(AxiomReportType.GFPA)
    if (!isStoredInSession) {
      fetchColumnDefByReportType()
    } else {
      sessionStorage.removeItem(AxiomReportType.GFPA)
      fetchColumnDefByReportType()
    }
    fetchColumnDefByReportType()
  }, [selectedReport])

  useEffect(() => {
    gfpaSummary && gfpaSummary.length && setRowData(gfpaSummary)
  }, [gfpaSummary])

  useEffect(() => {
    return () => {
      dispatch(clearGFPASummary())
    }
  }, [])

  const fetchColumnDefByReportType = async () => {
    try {
      const colRes = await getColumnDefByReportType(AxiomReportType.GFPA)
      sessionStorage.setItem(AxiomReportType.GFPA, JSON.stringify(colRes))
    } catch (err) {}
  }

  // Daily Contribution Menu
  const [summaryMenuEl, setSummaryMenuEl] = React.useState<null | HTMLElement>(
    null,
  )
  const summaryMenu = Boolean(summaryMenuEl)
  const openSummaryMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSummaryMenuEl(event.currentTarget)
  }
  const closeSummaryMenu = () => {
    setSummaryMenuEl(null)
  }

  // Expand
  // const [isFactorExpanded, setFactorExpanded] = useState(false)

  return (
    <ReportDashboardLayout
      selectedReport={selectedReport}
      navigation={<GFPADashboardNavigation />}
    >
      <Box
        display='grid'
        // gridTemplateRows={isFactorExpanded ? '0 1fr' : 'auto auto'}
        gridTemplateRows='auto auto'
        //   gap={isFactorExpanded ? 0 : 4}
        gap={4}
        boxSizing='border-box'
      >
        <Box
          display='grid'
          gridTemplateColumns='auto 1fr'
          gap={4}
          boxSizing='border-box'
          // height={isFactorExpanded ? '0px' : 'auto'}
          height='auto'
          overflow='hidden'
        >
          <Box
            display='grid'
            gridTemplateColumns='1fr 1fr'
            gap={4}
            boxSizing='border-box'
          >
            <Card
              sx={{
                gridColumn: '1 / span 2',
              }}
            >
              <CardHeader
                title={<Typography variant='h3'>Total Return</Typography>}
              />
              <CardContent>
                {!loaderTR ? (
                  <Stack direction='row' gap={9} px={9} alignItems='center'>
                    {Object.keys(totalReturn)?.map(
                      (key: keyof IGfpaTotalReturn | string) => {
                        const colDetail = getColumnDetail(
                          key,
                          AxiomReportType.GFPA,
                        )
                        const value = totalReturn[key as keyof IGfpaTotalReturn]
                        const label = colDetail?.header?.split(' ')[0]
                        const displayValue = formatCellValue(
                          formatNumber(value, true, 2, 2),
                          colDetail?.format,
                        )
                        return (
                          <ValueCard
                            key={key}
                            label={label}
                            value={value}
                            displayValue={displayValue}
                            reportType={selectedReport.reportType}
                          />
                        )
                      },
                    )}
                  </Stack>
                ) : (
                  loader()
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader
                title={<Typography variant='h3'>Effective Duration</Typography>}
              />
              <CardContent>
                {!loaderDuration ? (
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='stretch'
                    gap={6}
                  >
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      width='100%'
                    >
                      <Typography variant='h4' color='text.secondary'>
                        {
                          getColumnDetail(
                            'edur_p',
                            AxiomReportType.GFPA,
                          ).header?.split(' ')[0]
                        }
                      </Typography>
                      <Typography variant='h4' color='text.primary'>
                        {formatCellValue(
                          formatNumber(duration.edur_p, true, 2, 2),
                          getColumnDetail('edur_p', AxiomReportType.GFPA)
                            .format,
                        )}
                      </Typography>
                    </Stack>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      width='100%'
                    >
                      <Typography variant='h4' color='text.secondary'>
                        {
                          getColumnDetail(
                            'edur_b',
                            AxiomReportType.GFPA,
                          ).header?.split(' ')[0]
                        }
                      </Typography>
                      <Typography variant='h4' color='text.primary'>
                        {formatCellValue(
                          formatNumber(duration.edur_b, true, 2, 2),
                          getColumnDetail('edur_p', AxiomReportType.GFPA)
                            .format,
                        )}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  loader()
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader
                title={<Typography variant='h3'>Spread Duration</Typography>}
              />
              <CardContent>
                {!loaderDuration ? (
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='stretch'
                    gap={6}
                  >
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      width='100%'
                    >
                      <Typography variant='h4' color='text.secondary'>
                        {
                          getColumnDetail(
                            'sdur_p',
                            AxiomReportType.GFPA,
                          ).header?.split(' ')[0]
                        }
                      </Typography>
                      <Typography variant='h4' color='text.primary'>
                        {formatCellValue(
                          formatNumber(duration.sdur_p, true, 2, 2),
                          getColumnDetail('edur_p', AxiomReportType.GFPA)
                            .format,
                        )}
                      </Typography>
                    </Stack>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      width='100%'
                    >
                      <Typography variant='h4' color='text.secondary'>
                        {
                          getColumnDetail(
                            'sdur_b',
                            AxiomReportType.GFPA,
                          ).header?.split(' ')[0]
                        }
                      </Typography>
                      <Typography variant='h4' color='text.primary'>
                        {formatCellValue(
                          formatNumber(duration.sdur_b, true, 2, 2),
                          getColumnDetail('edur_p', AxiomReportType.GFPA)
                            .format,
                        )}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  loader()
                )}
              </CardContent>
            </Card>
          </Box>
          <Card sx={{ boxSizing: 'border-box' }}>
            <CardHeader
              sx={{ pb: 0 }}
              title={<Typography variant='h3'>Daily Returns</Typography>}
              action={
                <>
                  <IconButton
                    onClick={openSummaryMenu}
                    aria-controls={summaryMenu ? 'summary-menu' : undefined}
                  >
                    <MoreVert fontSize='inherit' />
                  </IconButton>
                  <Menu
                    id='basic-menu'
                    anchorEl={summaryMenuEl}
                    open={summaryMenu}
                    onClose={closeSummaryMenu}
                    MenuListProps={{
                      'aria-labelledby': 'summary-menu',
                      style: { minWidth: '15rem' },
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem
                      onClick={() =>
                        navigate(
                          `/reports/${selectedReport.reportId}/gfpa/daily-attribution`,
                        )
                      }
                    >
                      View Details
                    </MenuItem>
                  </Menu>
                </>
              }
            />
            <CardContent
              sx={{
                boxSizing: 'border-box',
                height: '100%sta',
                pt: 0,
                pb: 0,
              }}
            >
              {showLoader ? (
                <LoadingCard />
              ) : rowData.length ? (
                <GFPASummaryChart chartData={rowData} />
              ) : (
                <NoDataToDisplayCard />
              )}
            </CardContent>
          </Card>
        </Box>
        <Card
          sx={{
            boxSizing: 'border-box',
            // height: isFactorExpanded
            //   ? document.body.clientHeight - 280
            //   : 'auto',
            // overflow: isFactorExpanded ? 'auto' : 'inherit',
            height: 'auto',
            overflow: 'inherit',
          }}
        >
          <CardHeader
            title={
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
                gap={4}
              >
                <Typography variant='h3'>Factor Attribution</Typography>
                <Divider orientation='vertical' variant='middle' flexItem />
                <Typography
                  variant='h5'
                  color='text.secondary'
                  fontWeight='fontWeightBold'
                >
                  All Factors
                </Typography>
              </Stack>
            }
            titleTypographyProps={{
              component: 'div',
              display: 'block',
            }}
            action={
              <Stack direction='row'>
                <GridActions gridApiRef={gridReference} />
                {/* <IconButton
                  size='small'
                  disableRipple
                  onClick={() => setFactorExpanded((expanded) => !expanded)}
                >
                  {isFactorExpanded ? (
                    <CloseFullscreen fontSize='inherit' />
                  ) : (
                    <OpenInFull fontSize='inherit' />
                  )}
                </IconButton> */}
              </Stack>
            }
            sx={{
              pb: 0,
            }}
          />
          <CardContent
            sx={{
              boxSizing: 'border-box',
              pt: 0,
            }}
          >
            <GFPAFactorContributionPage setGridReference={setGridReference} />
          </CardContent>
        </Card>
      </Box>
    </ReportDashboardLayout>
  )
}
